// Data access seam.
//
// Requires REACT_APP_API_BASE to be set (e.g. http://localhost:8080 in .env.local).
// If unset, fetch calls will throw and the dashboard will show an error state.

const API_BASE = process.env.REACT_APP_API_BASE;

// Thrown when the API itself rejects the token (expired, revoked, wrong
// audience) rather than a generic request failure — callers should prompt
// re-authentication instead of showing a data-fetch error banner.
export class ApiAuthError extends Error {}

// Thrown on a 403: the token is valid but the account isn't @dag.com. Distinct
// from ApiAuthError because the fix is different — re-authenticating won't
// change which org the user's account belongs to, so a caller that redirects to
// login on this error just loops back to the same 403.
export class ApiForbiddenError extends Error {}

export interface RevenuePayload {
  meta: {
    project: string;
    grain: string;
    windowStart: string;
    windowEnd: string;
    queriedAt: string;
  };
  kpis: {
    grossChargedUsd: number;
    refundedUsd: number;
    netRevenueUsd: number;
    resolvedRevenueUsd: number;
    revenuePerClientUsd: number;
    payingClients: number;
    distinctClients: number;
    activeSubscriptions: number;
    resolvedCoveragePct: number;
    mrrUsd: number | null;
    arrUsd: number | null;
  };
  byBusinessLine: { businessLine: string; revenueUsd: number; lines: number }[];
  productMix: { product: string; revenueUsd: number; share: number }[];
  revenueTrend: { day: string; revenueUsd: number }[];
  paymentsHealth: {
    day: string;
    paid: number;
    failed: number;
    refunded: number;
    refundedUsd: number;
  }[];
  revenuePaths: { path: string; charges: number; usd: number }[];
  mrrByBusinessLine: { businessLine: string; mrrUsd: number; arrUsd: number }[];
  dataQuality: {
    grossChargedUsd: number;
    refundedUsd: number;
    resolvedUsd: number;
    unknownOriginUsd: number;
    unknownOriginCharges: number;
    wooChannelUsd: number;
    wooChannelCharges: number;
    unmappedBusinessLineUsd: number;
    unmappedProductIds: number;
  };
  feeBreakdown: {
    onboardingFeesUsd: number;
    aumFeesUsd: number | null;
  };
}

export async function fetchRevenue(
  grain?: string,
  from?: string,
  to?: string,
  getToken?: () => Promise<string>
): Promise<RevenuePayload> {
  if (!API_BASE) throw new Error('REACT_APP_API_BASE is not set');
  const qs = new URLSearchParams();
  if (grain) qs.set('grain', grain);
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);

  const headers: Record<string, string> = {};
  if (getToken) headers.Authorization = `Bearer ${await getToken()}`;

  const res = await fetch(`${API_BASE}/revenue?${qs.toString()}`, { headers });
  if (res.status === 401) throw new ApiAuthError(`revenue API ${res.status}`);
  if (res.status === 403) throw new ApiForbiddenError(`revenue API ${res.status}`);
  if (!res.ok) throw new Error(`revenue API ${res.status}`);
  return (await res.json()) as RevenuePayload;
}

// One flow row, shared by the trend / by-token / by-advisor breakdowns. Inflows
// and outflows are positive magnitudes; only net can be negative.
interface FlowAmounts {
  inflowsUsd: number;
  outflowsUsd: number;
  netUsd: number;
}

// Mirrors internal/dashboard.WealthPayload — the KPI families served by one
// GET /wealth: account size/holdings, advisor activity, and money movement.
export interface WealthPayload {
  meta: {
    project: string;
    grain: string;
    windowStart: string;
    windowEnd: string;
    queriedAt: string;
  };
  byDate: {
    day: string;
    avgMarketValueUsd: number | null;
    avgNetFundedUsd: number | null;
    accountCount: number | null;
    fundedAccountCount: number | null;
  }[];
  byToken: {
    token: string;
    avgHolding: number | null;
    avgNotionalUsd: number | null;
  }[];
  meetingsTrend: { bucket: string; meetings: number }[];
  meetingsByAdvisor: { advisor: string; meetings: number }[];
  fundedAccountsTrend: { bucket: string; count: number }[];
  riaOnboardedTrend: { bucket: string; count: number }[];
  flowKpis: {
    grossInflowsUsd: number;
    grossOutflowsUsd: number;
    netFlowUsd: number;
    inflowTransactions: number;
    outflowTransactions: number;
    accountsWithFlow: number;
    netNewAumUsd: number;
    netNewAumUsdPriorPeriod: number;
    netNewAumUsdDeltaPct: number | null;
  };
  flowTrend: ({ bucket: string } & FlowAmounts)[];
  flowsByToken: ({ token: string } & FlowAmounts)[];
  flowsByAdvisor: ({ repCode: string; advisorName: string } & FlowAmounts)[];
  flowsByFund: ({ fund: string } & FlowAmounts)[];
  flowDataQuality: {
    unclassifiedTransactions: number;
    unclassifiedActions: string[];
  };
  allocationDataQuality: {
    misallocatedAccounts: number;
    misallocatedAccountIds: string[];
  };
  aumTrend: { bucket: string; custodiedAumUsd: number | null; totalAumUsd: number | null }[];
  aumByCustodian: { custodian: string; custodyScope: string; aumUsd: number }[];
  aumByAsset: { asset: string; aumUsd: number; assetQty: number }[];
}

// Mirrors internal/dashboard.GrowthPayload — served by GET /growth. Every count
// is cumulative as of the end of its bucket; `both` is distinct emails present
// in both client tables.
export interface GrowthPayload {
  meta: {
    project: string;
    grain: string;
    windowStart: string;
    windowEnd: string;
    queriedAt: string;
  };
  clientGrowth: {
    bucket: string;
    privateClients: number;
    wealthClients: number;
    both: number;
  }[];
}

export async function fetchGrowth(
  grain?: string,
  from?: string,
  to?: string,
  getToken?: () => Promise<string>
): Promise<GrowthPayload> {
  if (!API_BASE) throw new Error('REACT_APP_API_BASE is not set');
  const qs = new URLSearchParams();
  if (grain) qs.set('grain', grain);
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);

  const headers: Record<string, string> = {};
  if (getToken) headers.Authorization = `Bearer ${await getToken()}`;

  const res = await fetch(`${API_BASE}/growth?${qs.toString()}`, { headers });
  if (res.status === 401) throw new ApiAuthError(`growth API ${res.status}`);
  if (res.status === 403) throw new ApiForbiddenError(`growth API ${res.status}`);
  if (!res.ok) throw new Error(`growth API ${res.status}`);
  return (await res.json()) as GrowthPayload;
}

// Mirrors internal/dashboard.LlcSalesPayload — served by GET /llc-sales, kept
// apart from /revenue because it reads the DAG App live, so an outage fails only it.
// Gross, before refunds; counts are products purchased (line items).
export interface LlcSalesTotals {
  newUsd: number;
  renewalUsd: number;
  mrrUsd: number;
  newCount: number;
  renewalCount: number;
  mrrCount: number;
}

export interface LlcSalesPayload {
  meta: {
    project: string;
    grain: string;
    windowStart: string;
    windowEnd: string;
    queriedAt: string;
  };
  totals: LlcSalesTotals;
  trend: (LlcSalesTotals & { bucket: string })[];
}

export async function fetchLlcSales(
  grain?: string,
  from?: string,
  to?: string,
  getToken?: () => Promise<string>
): Promise<LlcSalesPayload> {
  if (!API_BASE) throw new Error('REACT_APP_API_BASE is not set');
  const qs = new URLSearchParams();
  if (grain) qs.set('grain', grain);
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);

  const headers: Record<string, string> = {};
  if (getToken) headers.Authorization = `Bearer ${await getToken()}`;

  const res = await fetch(`${API_BASE}/llc-sales?${qs.toString()}`, { headers });
  if (res.status === 401) throw new ApiAuthError(`llc sales API ${res.status}`);
  if (res.status === 403) throw new ApiForbiddenError(`llc sales API ${res.status}`);
  if (!res.ok) throw new Error(`llc sales API ${res.status}`);
  return (await res.json()) as LlcSalesPayload;
}

// Mirrors internal/dashboard.OperationsPayload — served by GET /operations. A
// snapshot of the open-ticket backlog right now, so it takes no grain or date
// range. Ages are in days and null when there are no open tickets.
export interface OperationsPayload {
  meta: {
    project: string;
    queriedAt: string;
  };
  ticketAge: {
    openTickets: number;
    oldestDays: number | null;
    p90Days: number | null;
    medianDays: number | null;
  };
  // Lifetime revenue of everyone who submitted a ticket in the last 30 days,
  // divided by the number of those submitters. null when there were no
  // submitters in the window; 0 when there were submitters but no revenue.
  ticketSubmitterRevenue: {
    ticketSubmitters: number;
    avgRevenueUsd: number | null;
  };
  ticketCounts: {
    last7Days: number;
    last30Days: number;
    last90Days: number;
  };
}

export async function fetchOperations(
  getToken?: () => Promise<string>
): Promise<OperationsPayload> {
  if (!API_BASE) throw new Error('REACT_APP_API_BASE is not set');

  const headers: Record<string, string> = {};
  if (getToken) headers.Authorization = `Bearer ${await getToken()}`;

  const res = await fetch(`${API_BASE}/operations`, { headers });
  if (res.status === 401) throw new ApiAuthError(`operations API ${res.status}`);
  if (res.status === 403) throw new ApiForbiddenError(`operations API ${res.status}`);
  if (!res.ok) throw new Error(`operations API ${res.status}`);
  return (await res.json()) as OperationsPayload;
}

// Mirrors internal/dashboard.RetentionPayload — served by GET /retention. Carries
// revenue retention (Stripe MRR) and asset retention (Anchorage AUM) side by side;
// they answer different questions and are not derived from each other. Rates are
// null when the period has no starting book/snapshot, so charts draw a gap.
export interface RevenueRetentionFigures {
  startingMrrUsd: number;
  churnedMrrUsd: number;
  startingSubscriptions: number;
  churnedSubscriptions: number;
  grossRetentionPct: number | null;
  churnPct: number | null;
  logoChurnPct: number | null;
}

export interface AssetRetentionFigures {
  startingAumUsd: number | null;
  inflowsUsd: number;
  outflowsUsd: number;
  grossRetentionPct: number | null;
  netRetentionPct: number | null;
}

export interface RetentionPayload {
  meta: {
    project: string;
    grain: string;
    windowStart: string;
    windowEnd: string;
    queriedAt: string;
  };
  revenue: {
    kpis: RevenueRetentionFigures;
    trend: ({ bucket: string } & RevenueRetentionFigures)[];
    byBusinessLine: ({ businessLine: string } & RevenueRetentionFigures)[];
  };
  assets: {
    kpis: AssetRetentionFigures;
    trend: ({ bucket: string } & AssetRetentionFigures)[];
  };
}

export async function fetchRetention(
  grain?: string,
  from?: string,
  to?: string,
  getToken?: () => Promise<string>
): Promise<RetentionPayload> {
  if (!API_BASE) throw new Error('REACT_APP_API_BASE is not set');
  const qs = new URLSearchParams();
  if (grain) qs.set('grain', grain);
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);

  const headers: Record<string, string> = {};
  if (getToken) headers.Authorization = `Bearer ${await getToken()}`;

  const res = await fetch(`${API_BASE}/retention?${qs.toString()}`, { headers });
  if (res.status === 401) throw new ApiAuthError(`retention API ${res.status}`);
  if (res.status === 403) throw new ApiForbiddenError(`retention API ${res.status}`);
  if (!res.ok) throw new Error(`retention API ${res.status}`);
  return (await res.json()) as RetentionPayload;
}

export async function fetchWealth(
  grain?: string,
  from?: string,
  to?: string,
  getToken?: () => Promise<string>
): Promise<WealthPayload> {
  if (!API_BASE) throw new Error('REACT_APP_API_BASE is not set');
  const qs = new URLSearchParams();
  if (grain) qs.set('grain', grain);
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);

  const headers: Record<string, string> = {};
  if (getToken) headers.Authorization = `Bearer ${await getToken()}`;

  const res = await fetch(`${API_BASE}/wealth?${qs.toString()}`, { headers });
  if (res.status === 401) throw new ApiAuthError(`wealth API ${res.status}`);
  if (res.status === 403) throw new ApiForbiddenError(`wealth API ${res.status}`);
  if (!res.ok) throw new Error(`wealth API ${res.status}`);
  return (await res.json()) as WealthPayload;
}
